import NewsletterAPI from '../../api'
import { showPromptDialogAsync } from 'aldoo-ra/PromptDialog'

/**
 * ReleaseActionsController
 * Handles all release actions (start, pause, resume, stop, delete, reschedule)
 */
class ReleaseActionsController {
  /**
   * Start a scheduled release
   */
  async startRelease(releaseId) {
    const confirmed = await showPromptDialogAsync({
      variant: "admin",
      title: 'Start Release Now?',
      message: 'This will start sending emails immediately, bypassing the scheduled time.',
      confirmText: 'Start Now',
      cancelText: 'Cancel',
      type: 'warning'
    })

    if (!confirmed) return { cancelled: true }

    try {
      const response = await NewsletterAPI.startRelease(releaseId)

      if (!response.success) {
        throw new Error(response.error || 'Failed to start release')
      }

      return { success: true, message: 'Release started successfully' }
    } catch (error) {
      console.error('Start release error:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Pause a running release
   */
  async pauseRelease(releaseId) {
    try {
      const response = await NewsletterAPI.pauseRelease(releaseId)

      if (!response.success) {
        throw new Error(response.error || 'Failed to pause release')
      }

      return { success: true, message: 'Release paused successfully' }
    } catch (error) {
      console.error('Pause release error:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Resume a paused release
   */
  async resumeRelease(releaseId) {
    try {
      const response = await NewsletterAPI.resumeRelease(releaseId)

      if (!response.success) {
        throw new Error(response.error || 'Failed to resume release')
      }

      return { success: true, message: 'Release resumed successfully' }
    } catch (error) {
      console.error('Resume release error:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Stop a running release
   */
  async stopRelease(releaseId) {
    const confirmed = await showPromptDialogAsync({
      variant: "admin",
      title: 'Stop Release?',
      message: 'This will stop the release and mark remaining emails as unsent. This action cannot be undone.',
      confirmText: 'Stop Release',
      cancelText: 'Cancel',
      type: 'danger'
    })

    if (!confirmed) return { cancelled: true }

    try {
      const response = await NewsletterAPI.cancelRelease(releaseId)

      if (!response.success) {
        throw new Error(response.error || 'Failed to stop release')
      }

      return { success: true, message: 'Release stopped successfully' }
    } catch (error) {
      console.error('Stop release error:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Delete a release
   */
  async deleteRelease(releaseId, releaseName) {
    const confirmed = await showPromptDialogAsync({
      variant: "admin",
      title: 'Delete Release?',
      message: `Are you sure you want to delete "${releaseName}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger'
    })

    if (!confirmed) return { cancelled: true }

    try {
      const response = await NewsletterAPI.deleteRelease({ releaseId })

      if (!response.success) {
        throw new Error(response.error || 'Failed to delete release')
      }

      return { success: true, message: 'Release deleted successfully' }
    } catch (error) {
      console.error('Delete release error:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Reschedule a release
   */
  async rescheduleRelease(releaseId, currentScheduledAt) {
    // Note: showPromptDialogAsync doesn't support input fields
    // This needs a custom input dialog implementation
    return { cancelled: true }
  }
}

export default new ReleaseActionsController()
